import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { DEFAULT_FEE_ATTACHMENT_BODY } from 'citadel-shared/variables.js'

import { replaceConfigVariables } from '../../util/variableFormatting'
import { communicationTemplateSchema } from '../../schemas/communicationSchema'
import { Form, FlexEndContainer, OutlineContainer, SpaceBetweenRowContainer, FlexColumn } from '../styles/Containers'
import { SecondaryButton } from '../common/Button'
import { MedBodyText } from '../styles/Text'
import CommunicationForm from '../communications/CommunicationForm'
import SaveCommunicationTemplateButton from './SaveCommunicationTemplateButton'
import RichTextReadOnly from '../inputFields/RichTextReadOnly'

const HeaderRow = styled(SpaceBetweenRowContainer)`
  min-height: 37px;
  align-items: center;
`

function formValuesFromTemplate(communicationTemplate) {
  return {
    name: communicationTemplate?.name ?? '',
    body: communicationTemplate?.body ?? ''
  }
}

function trimId(value) {
  return String(value ?? '').trim()
}

function persistedTemplateIdForRequest(propId, idFromLastSuccessfulPost) {
  const fromProps = trimId(propId)
  if (fromProps !== '') return fromProps
  return trimId(idFromLastSuccessfulPost)
}

const emptyTemplate = {
  name: '',
  body: ''
}

function CommunicationTemplateEditable({
  children,
  communicationTemplate = emptyTemplate,
  isCreating = false,
  publicConfig = {},
  handleUpdateTemplate = () => {},
  handleCancelUpdateTemplate = () => {}
}) {
  const [baseline, setBaseline] = useState(() => formValuesFromTemplate(communicationTemplate))
  const [payloadToSave, setPayloadToSave] = useState(null)
  const [isSaveConfirmationOpen, setIsSaveConfirmationOpen] = useState(false)
  const [templateIdFromPostResponse, setTemplateIdFromPostResponse] = useState(null)
  const previousIsCreatingRef = useRef(isCreating)

  const formMethods = useForm({
    resolver: yupResolver(communicationTemplateSchema),
    defaultValues: formValuesFromTemplate(communicationTemplate)
  })

  const { reset, handleSubmit } = formMethods

  useEffect(() => {
    const values = formValuesFromTemplate(communicationTemplate)
    setBaseline(values)
    reset(values)
  }, [communicationTemplate.id, communicationTemplate.name, communicationTemplate.body, reset])

  useEffect(() => {
    if (trimId(communicationTemplate?.id) !== '') {
      setTemplateIdFromPostResponse(null)
    }
  }, [communicationTemplate.id])

  useEffect(() => {
    if (isCreating && previousIsCreatingRef.current === false) {
      setTemplateIdFromPostResponse(null)
    }
    previousIsCreatingRef.current = isCreating
  }, [isCreating])

  const onError = (errors, e) => console.error('Error', errors, e)

  function handleCancel() {
    reset(baseline)
    setIsSaveConfirmationOpen(false)
    setPayloadToSave(null)
    handleCancelUpdateTemplate()
  }

  function handleCloseSaveConfirmation() {
    setIsSaveConfirmationOpen(false)
    setPayloadToSave(null)
  }

  function onSubmit(data) {
    setPayloadToSave({ name: data.name, body: data.body })
    setIsSaveConfirmationOpen(true)
  }

  function handleSaveSuccess(saved) {
    const next = { name: saved.name, body: saved.body }
    setBaseline(next)
    reset(next)
    if (persistedTemplateIdForRequest(communicationTemplate?.id, templateIdFromPostResponse) === '') {
      setTemplateIdFromPostResponse(saved.id)
    }
    handleUpdateTemplate(saved)
  }

  const templateId = persistedTemplateIdForRequest(communicationTemplate?.id, templateIdFromPostResponse)
  const normalizedFeeAttachmentBody = replaceConfigVariables({ html: DEFAULT_FEE_ATTACHMENT_BODY, publicConfig })

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(onSubmit, onError)} gap="10px">
        <HeaderRow gap="10px">
          {children}
          <FlexEndContainer gap="5px">
            <SecondaryButton onClick={handleCancel} disabled={isSaveConfirmationOpen}>
              Cancel
            </SecondaryButton>
            <SaveCommunicationTemplateButton
              payload={payloadToSave ?? emptyTemplate}
              templateId={templateId === '' ? undefined : templateId}
              open={isSaveConfirmationOpen}
              disabled={isSaveConfirmationOpen}
              onClose={handleCloseSaveConfirmation}
              onSuccess={handleSaveSuccess}
            />
          </FlexEndContainer>
        </HeaderRow>
        <OutlineContainer padding="25px 30px">
          <FlexColumn>
            <CommunicationForm disabled={isSaveConfirmationOpen} />
            <FlexColumn gap="10px">
              <MedBodyText>The following text will be automatically included in your communication when a fee is attached:</MedBodyText>
              <RichTextReadOnly value={normalizedFeeAttachmentBody} />
            </FlexColumn>
          </FlexColumn>
        </OutlineContainer>
      </Form>
    </FormProvider>
  )
}

CommunicationTemplateEditable.propTypes = {
  children: PropTypes.node,
  communicationTemplate: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    body: PropTypes.string
  }),
  isCreating: PropTypes.bool,
  publicConfig: PropTypes.object,
  handleUpdateTemplate: PropTypes.func,
  handleCancelUpdateTemplate: PropTypes.func
}

export default CommunicationTemplateEditable
